import { useMutation, useQueryClient } from "@tanstack/react-query";
import { reportMutations } from "../../../mutations";
import { reportQueries } from "../../../queries";
import type { ReportDetailPresentation } from "../../../queries";

interface UpdateReportMetadataParams {
  reportId: string;
  displayName: string;
  context: string;
}

export function useUpdateReportMetadata() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      reportId,
      displayName,
      context,
    }: UpdateReportMetadataParams) => {
      const { error } = await reportMutations.analysisSetting.updateMetadata(
        reportId,
        { displayName, context },
      );
      if (error) throw error;
      return { reportId, displayName, context };
    },
    onMutate: async ({ reportId, displayName, context }) => {
      const presentationQueryKey = [
        ...reportQueries.detailPresentations().queryKey,
        reportId,
      ];
      await queryClient.cancelQueries({ queryKey: presentationQueryKey });

      const previous = queryClient.getQueriesData<ReportDetailPresentation>({
        queryKey: presentationQueryKey,
      });

      queryClient.setQueriesData<ReportDetailPresentation>(
        { queryKey: presentationQueryKey },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            analysisSetting: {
              ...old.analysisSetting,
              display_name: displayName,
              display_description: context,
            },
          };
        },
      );

      return { previous };
    },
    onError: (_error, _variables, context) => {
      for (const [queryKey, data] of context?.previous ?? []) {
        queryClient.setQueryData(queryKey, data);
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries(reportQueries.lists());
    },
  });
}
